import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  Repeat,
  TrendingUp,
  Bot,
  Target,
  Wallet,
  User,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Search,
  Star,
  Activity,
  BarChart3,
  Layers,
  Info,
  Globe,
  Bell,
  Shield,
  Zap,
  DollarSign,
  Bitcoin,
  Eye,
  EyeOff,
  Gift,
  CreditCard
} from 'lucide-react';
import { TradingMode, UserStatus } from '../App';
import { MarketData } from '../hooks/useDatabase';
import { User as UserType } from '@supabase/supabase-js';
import { useMarketData } from '../contexts/MarketDataContext';
import { Editorial, Typography, BorderRadius } from '../styles/primitives';
import {
  DISPLAY_CURRENCIES,
  DisplayCurrency,
  convertUsdValue,
  getDisplayCurrencySymbol
} from '../utils/displayCurrency';
import { DASHBOARD_PATHS } from '../routes/dashboardRoutes';
import { LISTED_CFD_INSTRUMENTS, matchesCfdSearch } from '../../supabase/functions/_shared/cfdInstruments';

interface HeaderProps {
  tradingMode: TradingMode;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  currentPrice: number;
  marketData?: MarketData;
  usdtBalance: number;
  btcBalance: number;
  totalPortfolioValue: number;
  user: UserType;
  signOut: () => void;
  marketDataList: MarketData[];
  userStatus: UserStatus;
  isDemoAccount: boolean;
  selectedDisplayCurrency: DisplayCurrency;
  setSelectedDisplayCurrency: (currency: DisplayCurrency) => void;
}

const Header: React.FC<HeaderProps> = ({
  tradingMode,
  selectedPair,
  setSelectedPair,
  currentPrice,
  marketData: selectedMarketData,
  usdtBalance,
  btcBalance,
  totalPortfolioValue,
  user,
  signOut,
  marketDataList,
  userStatus,
  isDemoAccount,
  selectedDisplayCurrency,
  setSelectedDisplayCurrency
}) => {
  const { t } = useTranslation();
  const { marketData, isConnected: realtimeConnected, getPriceBySymbol } = useMarketData();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBalances, setShowBalances] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPairSelector, setShowPairSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const currencyRef = useRef<HTMLDivElement>(null);

  const currencyOptions = DISPLAY_CURRENCIES;

  const selectedCurrencySymbol = getDisplayCurrencySymbol(selectedDisplayCurrency);

  const convertBalance = (balance: number, targetCurrency: DisplayCurrency): number =>
    convertUsdValue(balance, targetCurrency, getPriceBySymbol);

  const notificationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const pairSelectorRef = useRef<HTMLDivElement>(null);

  const getCurrentPairPrice = () => {
    const price = getPriceBySymbol(selectedPair);
    if (price > 0) return price;
    return currentPrice || 0;
  };

  const currentPairPrice = getCurrentPairPrice();
  const currentBtcPrice = getPriceBySymbol('BTCUSDT') || 0;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (pairSelectorRef.current && !pairSelectorRef.current.contains(event.target as Node)) {
        setShowPairSelector(false);
        setSearchTerm('');
      }
      if (currencyRef.current && !currencyRef.current.contains(event.target as Node)) {
        setIsCurrencyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const navigationItems: Array<{ key: TradingMode; label: string; icon: React.ElementType }> = [
    { key: 'home', label: t('navigation.home'), icon: Home },
    { key: 'swap', label: t('trading.swap'), icon: Repeat },
    { key: 'futures', label: t('trading.futures'), icon: TrendingUp },
    { key: 'cfd', label: t('trading.cfd'), icon: BarChart3 },
    { key: 'exchanges', label: 'Exchanges', icon: Globe },
    { key: 'exchange_arbitrage', label: 'Exchange Arb', icon: Activity },
    { key: 'prop_firm', label: t('propFirm.prop'), icon: Target },
    { key: 'robot', label: t('trading.robot'), icon: Bot },
    { key: 'events', label: t('trading.events'), icon: Activity },
    { key: 'staking', label: t('trading.staking'), icon: Layers },
    { key: 'wheel', label: 'Spin Wheel', icon: Gift }
  ];

  const getStatusColor = (status: UserStatus) => {
    const tierStatuses = ['No-Coiner', 'Shrimp', 'Crab', 'Octopus', 'Dolphin', 'Shark', 'Whale', 'Humpback'];
    if (tierStatuses.includes(status)) {
      return Editorial.Red;
    }
    return Editorial.Ink;
  };

  const getStatusIcon = (status: UserStatus) => {
    switch (status) {
      case 'No-Coiner': return '🚫';
      case 'Shrimp': return '🦐';
      case 'Crab': return '🦀';
      case 'Octopus': return '🐙';
      case 'Dolphin': return '🐬';
      case 'Shark': return '🦈';
      case 'Whale': return '🐋';
      case 'Humpback': return '🐳';
      default: return '📊';
    }
  };

  const formatPairDisplay = (symbol: string) => {
    if (symbol.endsWith('USDT')) {
      return symbol.replace('USDT', '/USDT');
    }
    return symbol;
  };

  const getDecimalPlaces = (symbol: string, price: number) => {
    if (symbol.endsWith('USDT')) {
      if (price < 0.01) return 8;
      if (price < 1) return 6;
      if (price < 10) return 4;
      return 2;
    }

    if (symbol.includes('/') && !symbol.includes('XAU') && !symbol.includes('XAG') && !symbol.includes('XPT') && !symbol.includes('XPD')) {
      return 5;
    }

    if (symbol.includes('XAU') || symbol.includes('XAG') || symbol.includes('XPT') || symbol.includes('XPD') ||
        symbol.includes('NATGAS') || symbol.includes('BCO') || symbol.includes('WTICO') ||
        symbol.includes('CORN') || symbol.includes('WHEAT') || symbol.includes('SOYBN') ||
        symbol.includes('SUGAR') || symbol.includes('COFFEE') || symbol.includes('COTTON') ||
        symbol.includes('COCOA')) {
      return 3;
    }

    return 3;
  };

  const formatPrice = (symbol: string, price: number) => {
    const decimals = getDecimalPlaces(symbol, price);
    return price.toFixed(decimals);
  };

  const getFilteredPairs = () => {
    if (tradingMode === 'cfd') {
      const HARDCODED_CFD_INSTRUMENTS = [
        ...LISTED_CFD_INSTRUMENTS,
        { symbol: 'EUR/USD', name: 'Euro/US Dollar', type: 'forex' },
        { symbol: 'GBP/USD', name: 'British Pound/US Dollar', type: 'forex' },
        { symbol: 'AUD/USD', name: 'Australian Dollar/US Dollar', type: 'forex' },
        { symbol: 'NZD/USD', name: 'New Zealand Dollar/US Dollar', type: 'forex' },
        { symbol: 'EUR/JPY', name: 'Euro/Japanese Yen', type: 'forex' },
        { symbol: 'GBP/JPY', name: 'British Pound/Japanese Yen', type: 'forex' },
        { symbol: 'EUR/GBP', name: 'Euro/British Pound', type: 'forex' },
        { symbol: 'EUR/AUD', name: 'Euro/Australian Dollar', type: 'forex' },
        { symbol: 'EUR/CHF', name: 'Euro/Swiss Franc', type: 'forex' },
        { symbol: 'GBP/AUD', name: 'British Pound/Australian Dollar', type: 'forex' },
        { symbol: 'GBP/CHF', name: 'British Pound/Swiss Franc', type: 'forex' },
        { symbol: 'AUD/CAD', name: 'Australian Dollar/Canadian Dollar', type: 'forex' },
        { symbol: 'AUD/CHF', name: 'Australian Dollar/Swiss Franc', type: 'forex' },
        { symbol: 'AUD/JPY', name: 'Australian Dollar/Japanese Yen', type: 'forex' },
        { symbol: 'CAD/JPY', name: 'Canadian Dollar/Japanese Yen', type: 'forex' },
        { symbol: 'CHF/JPY', name: 'Swiss Franc/Japanese Yen', type: 'forex' },
        { symbol: 'NZD/JPY', name: 'New Zealand Dollar/Japanese Yen', type: 'forex' },
        { symbol: 'XAG/USD', name: 'Silver/USD', type: 'commodity' },
        { symbol: 'NATGAS/USD', name: 'Natural Gas', type: 'commodity' },
        { symbol: 'BCO/USD', name: 'Brent Crude', type: 'commodity' },
        { symbol: 'WTICO/USD', name: 'WTI Crude Oil', type: 'commodity' },
        { symbol: 'XPT/USD', name: 'Platinum/USD', type: 'commodity' },
        { symbol: 'XAU/USD', name: 'Gold/USD', type: 'commodity' },
        { symbol: 'XPD/USD', name: 'Palladium/USD', type: 'commodity' },
        { symbol: 'CORN/USD', name: 'Corn', type: 'commodity' },
        { symbol: 'WHEAT/USD', name: 'Wheat', type: 'commodity' },
        { symbol: 'SOYBN/USD', name: 'Soybean', type: 'commodity' },
        { symbol: 'SUGAR/USD', name: 'Sugar', type: 'commodity' },
        { symbol: 'COFFEE/USD', name: 'Coffee', type: 'commodity' },
        { symbol: 'COTTON/USD', name: 'Cotton', type: 'commodity' },
        { symbol: 'COCOA/USD', name: 'Cocoa', type: 'commodity' },
        { symbol: 'AAPL', name: 'Apple Inc.', type: 'stock' },
        { symbol: 'MSFT', name: 'Microsoft Corporation', type: 'stock' },
        { symbol: 'GOOGL', name: 'Alphabet Inc.', type: 'stock' },
        { symbol: 'AMZN', name: 'Amazon.com Inc.', type: 'stock' },
        { symbol: 'TSLA', name: 'Tesla Inc.', type: 'stock' },
        { symbol: 'META', name: 'Meta Platforms Inc.', type: 'stock' },
        { symbol: 'NFLX', name: 'Netflix Inc.', type: 'stock' },
        { symbol: 'NVDA', name: 'NVIDIA Corporation', type: 'stock' },
        { symbol: 'BA', name: 'Boeing Company', type: 'stock' },
        { symbol: 'JPM', name: 'JPMorgan Chase & Co.', type: 'stock' },
        { symbol: 'GS', name: 'Goldman Sachs', type: 'stock' },
        { symbol: 'BAC', name: 'Bank of America', type: 'stock' },
        { symbol: 'WMT', name: 'Walmart', type: 'stock' },
        { symbol: 'DIS', name: 'Disney', type: 'stock' },
        { symbol: 'CVX', name: 'Chevron', type: 'stock' }
      ];

      if (!searchTerm) {
        return HARDCODED_CFD_INSTRUMENTS;
      }

      const term = searchTerm.toLowerCase();
      return HARDCODED_CFD_INSTRUMENTS.filter(data =>
        matchesCfdSearch(data, term)
      );
    }

    if (!searchTerm) {
      if (tradingMode === 'futures') {
        return marketData.filter(data => data.symbol.endsWith('USDT'));
      }
      return marketData;
    }

    const term = searchTerm.toLowerCase();

    if (tradingMode === 'futures') {
      return marketData.filter(data =>
        data.symbol.endsWith('USDT') && (
          data.symbol.toLowerCase().includes(term) ||
          formatPairDisplay(data.symbol).toLowerCase().includes(term)
        )
      );
    }

    return marketData.filter(data =>
      data.symbol.toLowerCase().includes(term) ||
      formatPairDisplay(data.symbol).toLowerCase().includes(term)
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:block fixed left-0 top-0 h-screen z-[200] overflow-y-auto hide-scrollbar ${isSidebarHovered ? 'w-64' : 'w-16'}`}
        style={{
          backgroundColor: '#FFFFFF',
          backdropFilter: 'blur(10px)',
          borderRight: '1px solid #e5e7eb',
          boxShadow: '0 0 20px rgba(0, 0, 0, 0.05)',
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onMouseEnter={() => {
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
          }
          setIsSidebarHovered(true);
        }}
        onMouseLeave={() => {
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }
          hoverTimeoutRef.current = setTimeout(() => {
            setIsSidebarHovered(false);
          }, 150);
        }}
      >
        {/* Logo */}
        <Link
          to={DASHBOARD_PATHS.home}
          className="p-4 flex items-center justify-center relative overflow-hidden w-full cursor-pointer"
        >
          <img
            src="/logo.svg"
            alt="Logo"
            className="h-6 absolute"
            style={{
              opacity: isSidebarHovered ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
              pointerEvents: isSidebarHovered ? 'auto' : 'none'
            }}
          />
          <span style={{
            color: '#111827',
            fontSize: '24px',
            fontWeight: Typography.WeightBold,
            opacity: isSidebarHovered ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out'
          }}>
            V
          </span>
        </Link>

        {/* Navigation */}
        <nav className="p-2">
          <div className="space-y-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.key}
                  to={DASHBOARD_PATHS[item.key]}
                  className={`w-full flex items-center gap-3 py-3 transition-all duration-200 ${isSidebarHovered ? 'px-4' : 'justify-center'}`}
                  style={{
                    color: tradingMode === item.key ? '#d91222' : '#6B7280',
                    backgroundColor: tradingMode === item.key ? 'rgba(217, 18, 34, 0.08)' : 'transparent',
                    fontSize: Typography.BodySmall,
                    fontWeight: tradingMode === item.key ? Typography.WeightMedium : Typography.WeightRegular,
                    borderLeft: tradingMode === item.key ? `3px solid #d91222` : '3px solid transparent',
                    borderRadius: '6px'
                  }}
                  onMouseEnter={(e) => {
                    if (tradingMode !== item.key) {
                      e.currentTarget.style.color = '#d91222';
                      e.currentTarget.style.backgroundColor = 'rgba(217, 18, 34, 0.04)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (tradingMode !== item.key) {
                      e.currentTarget.style.color = '#6B7280';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <Icon size={18} />
                  <span style={{
                    opacity: isSidebarHovered ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    width: isSidebarHovered ? 'auto' : '0',
                    maxWidth: isSidebarHovered ? '200px' : '0'
                  }}>
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>

        <Link
          to="/about"
          aria-label="About Valore"
          title="About Valore"
          className="flex items-center gap-3 px-5 py-3 text-neutral-600 hover:text-red-800"
        >
          <Info size={20} className="shrink-0" aria-hidden="true" />
          {isSidebarHovered && <span className="text-sm whitespace-nowrap">About Valore</span>}
        </Link>

        {/* Bottom Section - User Info */}
        <div className="absolute bottom-0 left-0 right-0 border-t" style={{
          backgroundColor: '#FFFFFF',
          borderColor: '#E5E7EB'
        }}>
          {isSidebarHovered && (
            <div className="p-4" style={{
              opacity: 1,
              transition: 'opacity 0.3s ease-in-out'
            }}>
                <div className="flex items-center justify-between mb-2">
                  <div style={{
                    color: '#6B7280',
                    fontSize: Typography.Caption
                  }}>
                    {t('header.portfolioValue')}
                  </div>
                  <button
                    onClick={() => setShowBalances(!showBalances)}
                    className="transition-colors"
                    style={{ color: '#9CA3AF' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#111827';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#9CA3AF';
                    }}
                  >
                    {showBalances ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="font-mono" style={{
                    color: '#111827',
                    fontSize: Typography.BodyMedium,
                    fontWeight: Typography.WeightMedium
                  }}>
                    {showBalances
                      ? `${selectedCurrencySymbol}${convertBalance(
                          totalPortfolioValue,
                          selectedDisplayCurrency
                        ).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '••••••'}
                  </div>
                  <div className="relative" ref={currencyRef}>
                    <button
                      onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                      className="flex items-center gap-1 px-2 py-1"
                      style={{
                        backgroundColor: 'transparent',
                        color: '#d91222',
                        fontSize: Typography.BodySmall,
                        borderRadius: '6px',
                        fontWeight: Typography.WeightMedium
                      }}
                    >
                      {selectedCurrencySymbol} <ChevronDown size={12} />
                    </button>
                    {isCurrencyDropdownOpen && (
                      <div
                        className="fixed bottom-20 left-20 w-44 z-50 max-h-80 overflow-y-auto border"
                        style={{
                          backgroundColor: '#FFFFFF',
                          borderColor: '#E5E7EB',
                          borderRadius: '6px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {currencyOptions.map((option) => (
                          <button
                            key={option.code}
                            onClick={() => {
                              setSelectedDisplayCurrency(option.code);
                              setIsCurrencyDropdownOpen(false);
                            }}
                            className="flex items-center w-full px-4 py-2 transition-colors"
                            style={{
                              backgroundColor: 'transparent',
                              color: selectedDisplayCurrency === option.code ? '#d91222' : '#6B7280',
                              borderLeft: selectedDisplayCurrency === option.code ? `2px solid #d91222` : '2px solid transparent',
                              fontSize: Typography.BodySmall
                            }}
                            onMouseEnter={(e) => {
                              if (selectedDisplayCurrency !== option.code) {
                                e.currentTarget.style.color = '#d91222';
                                e.currentTarget.style.backgroundColor = 'rgba(217, 18, 34, 0.04)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedDisplayCurrency !== option.code) {
                                e.currentTarget.style.color = '#6B7280';
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {option.symbol} {option.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Account Type */}
                <div className="mt-3 flex items-center gap-2">
                  <div
                    className="w-2 h-2"
                    style={{
                      backgroundColor: isDemoAccount ? '#9CA3AF' : '#d91222',
                      borderRadius: '6px'
                    }}
                  />
                  <span
                    style={{
                      fontSize: Typography.Caption,
                      fontWeight: Typography.WeightBold,
                      color: isDemoAccount ? '#6B7280' : '#111827',
                      letterSpacing: '0.05em'
                    }}
                  >
                    {isDemoAccount ? 'DEMO' : 'LIVE'}
                  </span>
                </div>
            </div>
          )}

          {isSidebarHovered && (
            <div className="p-4" style={{
              opacity: 1,
              transition: 'opacity 0.3s ease-in-out'
            }}>
              {/* User Status */}
              <div className="flex items-center gap-2">
                <span style={{ fontSize: Typography.BodyMedium }}>
                  {getStatusIcon(userStatus)}
                </span>
                <span style={{
                  fontSize: Typography.BodySmall,
                  fontWeight: Typography.WeightMedium,
                  color: '#d91222'
                }}>
                  {t(`header.tiers.${userStatus.toLowerCase()}`)}
                </span>
              </div>
            </div>
          )}

          {!isSidebarHovered && (
            <div style={{
              opacity: 1,
              transition: 'opacity 0.3s ease-in-out'
            }}>
              {/* Collapsed Wallet Icon */}
              <div className="p-4 flex justify-center">
                <Wallet size={18} style={{ color: '#6B7280' }} />
              </div>

              {/* Collapsed Status Icon */}
              <div className="p-4 flex justify-center">
                <span style={{ fontSize: Typography.BodyMedium }}>
                  {getStatusIcon(userStatus)}
                </span>
              </div>
            </div>
          )}

          {/* User Menu */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className={`w-full flex items-center p-4 transition-colors relative ${isSidebarHovered ? 'justify-between' : 'justify-center'}`}
              style={{
                color: '#6B7280',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#d91222';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6B7280';
              }}
            >
              {/* Expanded Menu */}
              <div
                className="flex items-center justify-between"
                style={{
                  position: isSidebarHovered ? 'relative' : 'absolute',
                  width: isSidebarHovered ? '100%' : undefined,
                  opacity: isSidebarHovered ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out',
                  pointerEvents: isSidebarHovered ? 'auto' : 'none'
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-8 h-8 flex items-center justify-center"
                    style={{
                      backgroundColor: 'rgba(217, 18, 34, 0.1)',
                      borderRadius: '6px'
                    }}
                  >
                    <User size={14} style={{ color: '#d91222' }} />
                  </div>
                  <span style={{
                    fontSize: Typography.BodySmall,
                    fontWeight: Typography.WeightMedium,
                    whiteSpace: 'nowrap'
                  }}>
                    Menu
                  </span>
                </div>
                <ChevronDown size={14} />
              </div>

              {/* Collapsed Icon */}
              {!isSidebarHovered && (
                <User size={18} style={{ color: '#6B7280' }} />
              )}
            </button>

            {showUserMenu && (
              <div
                className="absolute bottom-full left-0 right-0 mb-2 z-50 overflow-hidden border"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderColor: '#E5E7EB',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div className="p-4 border-b" style={{ borderColor: '#E5E7EB' }}>
                  <div style={{
                    fontWeight: Typography.WeightMedium,
                    color: '#111827',
                    fontSize: Typography.BodySmall,
                    wordBreak: 'break-all'
                  }}>
                    {user.email}
                  </div>
                  <div style={{
                    fontSize: Typography.Caption,
                    color: '#d91222'
                  }}>
                    {getStatusIcon(userStatus)} {userStatus}
                  </div>
                </div>

                <div className="p-2">
                  <Link
                    to={DASHBOARD_PATHS.wallet}
                    onClick={() => {
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 transition-colors flex items-center gap-3"
                    style={{
                      color: '#6B7280',
                      fontSize: Typography.BodySmall,
                      borderRadius: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#d91222';
                      e.currentTarget.style.backgroundColor = 'rgba(217, 18, 34, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#6B7280';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Wallet size={14} />
                    <span>{t('navigation.wallet')}</span>
                  </Link>

                  <Link
                    to={DASHBOARD_PATHS.profile}
                    onClick={() => {
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 transition-colors flex items-center gap-3"
                    style={{
                      color: '#6B7280',
                      fontSize: Typography.BodySmall,
                      borderRadius: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#d91222';
                      e.currentTarget.style.backgroundColor = 'rgba(217, 18, 34, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#6B7280';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <User size={14} />
                    <span>{t('navigation.profile')}</span>
                  </Link>

                  <button
                    onClick={() => {
                      signOut();
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-3 py-2 transition-colors flex items-center gap-3"
                    style={{
                      color: '#6B7280',
                      fontSize: Typography.BodySmall,
                      borderRadius: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#d91222';
                      e.currentTarget.style.backgroundColor = 'rgba(217, 18, 34, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#6B7280';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <LogOut size={14} />
                    <span>{t('navigation.signOut')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <header
        className="lg:hidden sticky top-0 z-40"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}
      >
        <div className="px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={DASHBOARD_PATHS.home} onClick={() => setIsMobileMenuOpen(false)}>
              <img
                src="/logo.svg"
                alt="Logo"
                className="h-6"
              />
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 transition-all duration-200"
              style={{
                color: 'rgba(0, 0, 0, 0.5)',
                borderRadius: '10px',
                background: isMobileMenuOpen ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#d91222';
                e.currentTarget.style.background = 'rgba(217, 18, 34, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'rgba(0, 0, 0, 0.5)';
                e.currentTarget.style.background = isMobileMenuOpen ? 'rgba(0, 0, 0, 0.04)' : 'transparent';
              }}
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="py-5 px-4"
            style={{
              background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
              borderTop: '1px solid rgba(0, 0, 0, 0.06)'
            }}
          >
            {/* Mobile Portfolio Value */}
            <div
              className="mb-5 p-4"
              style={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 0, 0, 0.06)',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)'
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(0, 0, 0, 0.5)',
                    marginBottom: '4px'
                  }}>
                    {t('header.portfolioValue')}
                  </div>
                  <div className="font-mono" style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    color: '#000'
                  }}>
                    {showBalances
                      ? `${selectedCurrencySymbol}${convertBalance(
                          totalPortfolioValue,
                          selectedDisplayCurrency
                        ).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : '••••••'}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowBalances(!showBalances)}
                    className="transition-all duration-200 p-2"
                    style={{
                      color: 'rgba(0, 0, 0, 0.4)',
                      background: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000';
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(0, 0, 0, 0.4)';
                      e.currentTarget.style.background = 'rgba(0, 0, 0, 0.04)';
                    }}
                  >
                    {showBalances ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <div className="relative" ref={currencyRef}>
                    <button
                      onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
                      className="flex items-center gap-1 px-2 py-1"
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: '#d91222',
                        fontSize: '12px',
                        borderRadius: '6px',
                        fontWeight: 600
                      }}
                    >
                      {selectedCurrencySymbol} <ChevronDown size={12} />
                    </button>
                    {isCurrencyDropdownOpen && (
                      <div
                        className="absolute top-full right-0 mt-2 w-44 z-50 max-h-80 overflow-y-auto border"
                        style={{
                          backgroundColor: '#FFFFFF',
                          borderColor: '#E5E7EB',
                          borderRadius: '6px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        {currencyOptions.map((option) => (
                          <button
                            key={option.code}
                            onClick={() => {
                              setSelectedDisplayCurrency(option.code);
                              setIsCurrencyDropdownOpen(false);
                            }}
                            className="flex items-center w-full px-4 py-2 transition-colors"
                            style={{
                              backgroundColor: 'transparent',
                              color: selectedDisplayCurrency === option.code ? '#d91222' : '#6B7280',
                              borderLeft: selectedDisplayCurrency === option.code ? `2px solid #d91222` : '2px solid transparent',
                              fontSize: '12px'
                            }}
                            onMouseEnter={(e) => {
                              if (selectedDisplayCurrency !== option.code) {
                                e.currentTarget.style.color = '#d91222';
                                e.currentTarget.style.backgroundColor = 'rgba(217, 18, 34, 0.04)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedDisplayCurrency !== option.code) {
                                e.currentTarget.style.color = '#6B7280';
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            {option.symbol} {option.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div
                    className="flex items-center gap-1.5 px-3 py-1.5"
                    style={{
                      background: 'rgba(217, 18, 34, 0.08)',
                      borderRadius: '20px'
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>
                      {getStatusIcon(userStatus)}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#d91222'
                    }}>
                      {userStatus}
                    </span>
                  </div>
                </div>
              </div>

              {/* Account Type Indicator - Mobile */}
              <div className="flex items-center justify-center">
                <div
                  className="px-4 py-2"
                  style={{
                    background: isDemoAccount ? 'rgba(0, 0, 0, 0.04)' : 'rgba(0, 0, 0, 0.06)',
                    borderRadius: '10px'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2"
                      style={{
                        backgroundColor: isDemoAccount ? 'rgba(0, 0, 0, 0.3)' : '#000',
                        borderRadius: '4px'
                      }}
                    />
                    <span
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: isDemoAccount ? 'rgba(0, 0, 0, 0.5)' : '#000',
                        letterSpacing: '0.08em'
                      }}
                    >
                      {isDemoAccount ? 'DEMO ACCOUNT' : 'LIVE ACCOUNT'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Navigation */}
            <div className="grid grid-cols-2 gap-2 mb-5">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = tradingMode === item.key;
                return (
                  <Link
                    key={item.key}
                    to={DASHBOARD_PATHS[item.key]}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2.5 px-4 py-3.5 transition-all duration-200"
                    style={{
                      background: isActive ? 'rgba(217, 18, 34, 0.08)' : 'rgba(0, 0, 0, 0.02)',
                      color: isActive ? '#d91222' : 'rgba(0, 0, 0, 0.6)',
                      border: isActive ? '1.5px solid rgba(217, 18, 34, 0.2)' : '1px solid rgba(0, 0, 0, 0.06)',
                      fontSize: '13px',
                      fontWeight: isActive ? 600 : 500,
                      borderRadius: '12px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = '#d91222';
                        e.currentTarget.style.background = 'rgba(217, 18, 34, 0.06)';
                        e.currentTarget.style.border = '1px solid rgba(217, 18, 34, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                        e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
                        e.currentTarget.style.border = '1px solid rgba(0, 0, 0, 0.06)';
                      }
                    }}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* User Menu Options - Mobile */}
            <div className="space-y-2">
              <Link
                to={DASHBOARD_PATHS.wallet}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3.5 transition-all duration-200 flex items-center gap-3"
                style={{
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  background: 'rgba(0, 0, 0, 0.02)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#d91222';
                  e.currentTarget.style.background = 'rgba(217, 18, 34, 0.06)';
                  e.currentTarget.style.border = '1px solid rgba(217, 18, 34, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.border = '1px solid rgba(0, 0, 0, 0.06)';
                }}
              >
                <Wallet size={16} />
                <span>{t('navigation.wallet')}</span>
              </Link>

              <Link
                to={DASHBOARD_PATHS.profile}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3.5 transition-all duration-200 flex items-center gap-3"
                style={{
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  background: 'rgba(0, 0, 0, 0.02)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#d91222';
                  e.currentTarget.style.background = 'rgba(217, 18, 34, 0.06)';
                  e.currentTarget.style.border = '1px solid rgba(217, 18, 34, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'rgba(0, 0, 0, 0.6)';
                  e.currentTarget.style.background = 'rgba(0, 0, 0, 0.02)';
                  e.currentTarget.style.border = '1px solid rgba(0, 0, 0, 0.06)';
                }}
              >
                <User size={16} />
                <span>{t('navigation.profile')}</span>
              </Link>

              <button
                onClick={() => {
                  signOut();
                  setIsMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-3.5 transition-all duration-200 flex items-center gap-3"
                style={{
                  color: '#d91222',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '12px',
                  border: '1px solid rgba(217, 18, 34, 0.15)',
                  background: 'rgba(217, 18, 34, 0.04)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(217, 18, 34, 0.1)';
                  e.currentTarget.style.border = '1px solid rgba(217, 18, 34, 0.25)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(217, 18, 34, 0.04)';
                  e.currentTarget.style.border = '1px solid rgba(217, 18, 34, 0.15)';
                }}
              >
                <LogOut size={16} />
                <span>{t('navigation.signOut')}</span>
              </button>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
